import { Box } from '@jfs/ui-new'
import { ButtonNew } from '../../components/AdminButton'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Container } from '../../components/Container'
import { DateTabs } from '../../components/DateTabs/DateTabs'
import { NewTable } from '../../components/NewTable'
import { useLazyApiData, useStores } from '../../data/store'
import { camelCaseToTitleCase, flattenObject } from '../../utils'
import { Pagination } from '../FraudAndRiskManagement/Pagination'

interface EssAuditTrialsProps {
  type: string
  title?: string
  showCurrentData?: boolean
  isApprover?: boolean
}

const EssAuditTrials: FC<EssAuditTrialsProps> = ({
  type,
  title = '',
  showCurrentData = true,
  isApprover,
}: EssAuditTrialsProps) => {
  const navigation = useNavigation()

  const { fetchAuditTrialList, isAccessConfigPriviledged, updateSelectedDate, selectedDateInfo, exportFileAsync } =
    useStores((store) => ({
      fetchAuditTrialList: store.fetchAuditTrialList,
      isAccessConfigPriviledged: store.user.getAccessConfig([
        'sys_control_config',
        'audit',
        'approval_audit_privileged',
        'isAccessible',
      ]),
      updateSelectedDate: store?.updateSelectedDateInfo,
      selectedDateInfo: store.selectedDateInfo,
      exportFileAsync: store.exportFileAsync,
    }))
  const [isExpanded, setisExpanded] = useState<{ isExpanded: boolean; value: number }>({ isExpanded: false, value: -1 })
  const [selectedData, setSelectedData] = useState(null)
  const [approvalData, setApprovalData] = useState({})
  const abortControllerRef = useRef<AbortController>()
  const [paginationInfo, setPaginationInfo] = useState({
    dataWindow: 10,
    pageNumber: 1,
    total: 0,
  })
  const getAbortController = () => {
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current
  }

  const onDateSelector = (fromDate: Date, toDate: Date) => {
    updateSelectedDate({
      ...selectedDateInfo,
      fromDate,
      toDate,
    })
    lazyGetAuditTrailList()
  }

  const [listData, setListData] = useState<any>([])

  const [getAuditApiCaller, { state, data }] = useLazyApiData(fetchAuditTrialList)
  const [lazyExportAuditTrialList, exportAuditTrialHandler] = useLazyApiData(exportFileAsync)
  const lazyGetAuditTrailList = useCallback(
    (number?: any, dataWindow?: any) => {
      getAuditApiCaller(
        type,
        isAccessConfigPriviledged,
        isApprover,
        {
          number: number ?? paginationInfo.pageNumber,
          size: dataWindow ?? paginationInfo.dataWindow,
        },
        getAbortController,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigation],
  )

  useFocusEffect(
    useCallback(() => {
      getAuditApiCaller(
        type,
        isAccessConfigPriviledged,
        isApprover,
        {
          number: paginationInfo.pageNumber,
          size: paginationInfo.dataWindow,
        },
        getAbortController,
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigation]),
  )

  useEffect(() => {
    if (state === 'fulfilled') {
      const flattenData = data?.data?.map((item) => {
        return flattenObject(item, ['autoSettle', 'payoutSettleHold'])
      })
      setListData(flattenData)
      setPaginationInfo({ ...paginationInfo, total: data?.totalElements ?? 0, pageNumber: data?.pageNo })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const onDismiss = () => {
    setisExpanded({ isExpanded: false, value: -1 })
  }

  const keyExtractor = (item: any): string => {
    return '' + item.id
  }

  const headTitles = Object.keys(listData?.[0] ?? {}).reduce(
    (finalValue, key) => ({ ...finalValue, [key]: camelCaseToTitleCase(key) }),
    {},
  )

  const onPageClick = useCallback(
    (pageNumber: number) => {
      console.log('onPageClick pageNumber =', pageNumber)
      setPaginationInfo({ ...paginationInfo, pageNumber })
      lazyGetAuditTrailList(pageNumber, paginationInfo.dataWindow)
    },
    [lazyGetAuditTrailList, paginationInfo],
  )
  const onItemCountSelect = useCallback(
    (count: string) => {
      console.log('onItemsCountSelect count =', count)
      setPaginationInfo({ ...paginationInfo, pageNumber: 1, dataWindow: parseInt(count, 10) })
      lazyGetAuditTrailList(1, count)
    },
    [paginationInfo],
  )
  const exportApproval = useCallback(() => {
    lazyExportAuditTrialList(
      type,
      isAccessConfigPriviledged ? 'privileged' : isApprover ? 'approver' : 'user',
      isAccessConfigPriviledged,
      getAbortController,
    )
  }, [isAccessConfigPriviledged, isApprover, lazyExportAuditTrialList])

  const getDefaultMinDate = useCallback(() => {
    const date = new Date()
    date.setFullYear(2023)
    date.setMonth(0)
    date.setDate(1)
    return date
  }, [])

  return (
    <Box>
      <Box p={8} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <DateTabs
          onDateSelector={onDateSelector}
          disabled={false}
          sendError={() => {}}
          updateSelectedDate={updateSelectedDate}
          selectedDateInfo={selectedDateInfo}
          type={type}
          maxRange={365}
          defaultMinDate={getDefaultMinDate()}
        />
        <ButtonNew
          title={'Export'}
          onPress={exportApproval}
          height={28}
          borderRadius={4}
          width={128}
          isLoading={exportAuditTrialHandler.state === 'pending'}
        />
      </Box>
      <Container
        isLoading={state === 'pending'}
        nativeID="vertical-scroll"
        errorMessage={(data?.message as any) || (state === 'fulfilled' && data?.data?.length === 0 && 'No Data Found')}
        onRetry={() => lazyGetAuditTrailList()}
        height={undefined}
        flexGrow={1}
        minHeight={Math.max(listData?.length * 48 + 70, 300)}>
        <NewTable
          data={listData}
          rowHeight={48}
          headerHeight={57}
          headerRowKeys={headTitles}
          tableWidth={Object.keys(headTitles).length * 200}
          onRowClick={() => {}} // customWidthFunc={getCustomColumnWidth}
          // onRowClick={downloadEvidence}
          // onAction={(index: any, id: any, data) => downloadEvidence(data)}
        />
      </Container>
      <Pagination
        dataWindow={paginationInfo.dataWindow}
        onPageClick={onPageClick}
        onItemsCountSelect={onItemCountSelect}
        pageNumber={paginationInfo.pageNumber}
        totalPages={Math.ceil(paginationInfo.total / paginationInfo.dataWindow)}
        total={paginationInfo.total}
      />
    </Box>
  )
}

export { EssAuditTrials }
